import {
  Autocomplete,
  Box,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import React from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { getImageSrc } from '../../../../../helpers/getImageSrc'
import { autocompleteStyles as styles } from '../styles/styles'

// на будущее
export type OptionType = {
  key: string | number
  value: string
  iconSrc?: string
}

type CustomAutocompleteProps<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
  label?: string
  placeholder?: string
  handleChangeCallback?: (value: string) => FieldValues
  handleEditValue?: (value: FieldValues) => T
  multiple?: boolean
  fullWidth: boolean
  options: OptionType[] | string[]
  withoutTyping?: boolean
  fullDotationRegions?: string[]
  freeSolo?: boolean
  error?: boolean
  disabled?: boolean
  withUnknownValueError?: boolean
  onChange?: (value: string) => void
  withLabel?: boolean
  withClearBtn?: boolean
  fullHeight?: boolean
  onlyCustomError?: boolean
  withTooltip?: boolean
}

const FormAutocomplete = <T extends FieldValues>(
  props: CustomAutocompleteProps<T>,
) => {
  const {
    name,
    control,
    handleChangeCallback,
    label,
    placeholder,
    options,
    handleEditValue,
    withoutTyping,
    fullDotationRegions,
    error: customError,
    withUnknownValueError,
    freeSolo,
    onChange: customOnChange,
    withLabel,
    withClearBtn,
    fullHeight,
    onlyCustomError,
    withTooltip,
    ...otherProps
  } = props
  const [unknownValueError, setUnknownValueError] = React.useState(false)

  const selectPaper = (options: any) => (
    <Paper {...options} className={styles.paper} />
  )

  const renderOption = (props: any, option: any, value: any) => {
    const selected = value === option
    const isDotation = fullDotationRegions?.includes(option)

    return (
      <MenuItem
        value={option}
        className={classNames(
          styles.menuItem,
          selected && styles.menuItemSelected,
        )}
        sx={menuItemSx(withLabel, fullHeight)}
        {...props}
      >
        <Tooltip
          title={option}
          placement='top'
          disableHoverListener={!withTooltip}
        >
          <Box
            width={1}
            display={'flex'}
            flexWrap={'nowrap'}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Typography variant={'subtitle1'} sx={styles.currencyName}>
              {option}
            </Typography>
            {isDotation && (
              <img src={getImageSrc('full-subsidy')} alt='full subsidy icon' />
            )}
          </Box>
        </Tooltip>
      </MenuItem>
    )
  }

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { value, onChange, ...field },
        fieldState: { error },
      }) => {
        // !!работает только со строками!
        //  если value неизвестная строка а не сто-то выбранное будет посвечивать красным но не мешать отправке формы
        if (withUnknownValueError) {
          value && !options.includes(value as any) && setUnknownValueError(true)
          value &&
            !!options.includes(value as any) &&
            setUnknownValueError(false)
        }

        return (
          <Autocomplete
            options={options}
            value={handleEditValue ? handleEditValue(value) : value}
            onChange={(_e, value) => {
              handleChangeCallback
                ? onChange(handleChangeCallback(value))
                : onChange(value)

              customOnChange && customOnChange(value)
            }}
            disabled={otherProps.disabled}
            PaperComponent={selectPaper}
            noOptionsText={'Нет совпадений'}
            renderOption={(props, option) => renderOption(props, option, value)}
            clearIcon={null}
            onInputChange={(_e, value) => {
              handleChangeCallback
                ? onChange(handleChangeCallback(value))
                : onChange(value)

              customOnChange && customOnChange(value)
            }}
            popupIcon={
              <img
                alt={''}
                src={getImageSrc('downIcon')}
                style={popupIconStyle(otherProps)}
              />
            }
            renderInput={(params) => {
              return (
                <>
                  <TextField
                    {...params}
                    placeholder={placeholder}
                    label={label}
                    error={
                      onlyCustomError
                        ? customError
                        : !!error?.message || customError || unknownValueError
                    }
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                    }}
                    inputProps={{
                      ...params.inputProps,
                      readOnly: withoutTyping,
                    }}
                    sx={textFieldSx(withoutTyping)}
                    {...field}
                    {...otherProps}
                  />
                  {!!value && withClearBtn && (
                    <IconButton onClick={() => onChange('')} sx={iconBtnSx}>
                      <img
                        src={getImageSrc('close')}
                        alt={'Стереть'}
                        style={{
                          width: '24px',
                        }}
                      />
                    </IconButton>
                  )}
                </>
              )
            }}
            sx={{ position: 'relative' }}
          />
        )
      }}
    />
  )
}

const menuItemSx = (withLabel?: boolean, fullHeight?: boolean) => ({
  h6: {
    textWrap: withLabel && !fullHeight ? 'auto' : undefined,
    textOverflow: withLabel && !fullHeight ? 'initial' : 'ellipsis',
    lineHeight: withLabel && !fullHeight ? '1.2rem' : undefined,
  },
})

const popupIconStyle = (otherProps?: any) => ({
  height: '23px',
  width: '23px',
  marginRight: '7px',
  opacity: otherProps.disabled ? '.3' : '.8',
})

const textFieldSx = (withoutTyping?: boolean) => ({
  '.MuiFormControl-root': {
    position: 'relative',
  },
  input: {
    padding: '0 4px 0 6px!important',
    cursor: withoutTyping ? 'pointer' : 'text',
    overflow: 'hidden!important',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis !important',
    maxWidth: '100%',
    width: '90%!important',
  },
})

const iconBtnSx = {
  position: 'absolute',
  top: '6px',
  right: '40px',
  padding: '2px',
}

export default observer(FormAutocomplete)
